// SPDX-License-Identifier: MIT
// DebugLogger - flag-gated logging with redaction (Phase 5)

export interface DebugLoggerOptions {
  enabled?: boolean; // default derived from env VITE_AZURE_DEBUG
  redactKeys?: string[]; // keys to redact in objects
}

const DEFAULT_REDACT_KEYS = [
  'sas',
  'token',
  'authorization',
  'auth',
  'signature',
  'sig',
  'sp',
  'se',
  'sr',
];

interface RateWindow {
  ts: number;
}

export class DebugLogger {
  private enabled: boolean;
  private redact: Set<string>;
  private events: RateWindow[] = [];
  private static readonly MAX_EVENTS = 20; // per window
  private static readonly WINDOW_MS = 5000; // 5s window
  private dropped = 0;

  constructor(opts: DebugLoggerOptions = {}) {
    // import.meta typing may not include env so cast minimally
    const flag = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
      ?.VITE_AZURE_DEBUG;
    this.enabled = opts.enabled ?? (flag === '1' || flag === 'true');
    this.redact = new Set((opts.redactKeys ?? DEFAULT_REDACT_KEYS).map(k => k.toLowerCase()));
  }

  setEnabled(flag: boolean) {
    this.enabled = flag;
  }
  enable() {
    this.enabled = true;
  }
  disable() {
    this.enabled = false;
  }
  isEnabled() {
    return this.enabled;
  }

  private rateCheck(): boolean {
    const now = Date.now();
    this.events = this.events.filter(e => now - e.ts < DebugLogger.WINDOW_MS);
    if (this.events.length >= DebugLogger.MAX_EVENTS) {
      this.dropped++;
      return false; // rate limited
    }
    this.events.push({ ts: now });
    return true;
  }

  log(event: string, payload?: unknown) {
    if (!this.enabled) return;
    if (!this.rateCheck()) return;
    const safe = this.sanitize(payload);
    console.debug(`[AZURE][${event}]`, safe);
  }

  error(event: string, err: unknown, context?: unknown) {
    if (!this.enabled) return;
    if (!this.rateCheck()) return;
    const safeErr = this.sanitizeError(err);
    const safeCtx = this.sanitize(context);
    console.warn(`[AZURE][${event}][ERROR]`, safeErr, safeCtx);
  }

  private sanitize(value: unknown): unknown {
    if (value == null) return value;
    if (typeof value === 'string') {
      return this.redactString(value);
    }
    if (Array.isArray(value)) return value.map(v => this.sanitize(v));
    if (typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) {
        if (this.redact.has(k.toLowerCase())) {
          out[k] = '[REDACTED]';
        } else {
          out[k] = this.sanitize(v);
        }
      }
      return out;
    }
    return value;
  }

  private redactString(str: string): string {
    let out = str;
    if (out.length > 500) out = out.slice(0, 500) + '…';
    // Mask sig plus selected query params
    out = out.replace(/sig=([^&]+)/gi, 'sig=[REDACTED]');
    out = out.replace(/(sp|se|sr)=([^&]+)/gi, (m, k) => `${k}=[REDACTED]`);
    return out;
  }

  private sanitizeError(err: unknown) {
    if (!err) return err;
    const e = err as Partial<Record<string, unknown>> & {
      message?: string;
      code?: string;
      status?: number;
      statusCode?: number;
      category?: string;
      retryable?: boolean;
    };
    return {
      message: e.message,
      code: e.code,
      status: e.status || e.statusCode,
      category: e.category,
      retryable: e.retryable,
    };
  }

  // Test-only inspection (not part of public API). Guarded by NODE_ENV check to reduce accidental prod usage.
  __getStats() {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') {
      return { events: this.events.length, dropped: this.dropped };
    }
    return null;
  }
}
